import { SUPPORTED_DOCUMENT_FILE_TYPES } from "@/modules/application/common/constants";
import { ApplicationKeys } from "@/modules/application/common/enum";
import type { IUserOpeningApplicationForm } from "@/modules/application/common/types";
import { getRoleOptions } from "@/modules/application/helpers";
import {
  useCreateUserOpeningApplicationMutation,
  useGetUserOpeningApplicationQuery,
  useUpdateUserOpeningApplicationMutation
} from "@/modules/application/services";
import { INPUT_MAX_LIMIT, } from "@/shared/common/constants";
import type { IExtendedObject, IOperationPageProps } from "@/shared/common/types";
import { Select } from "@/shared/components";
import { ApplicationLayout } from "@/shared/components/ApplicationLayout";
import { isValidPhoneNumber, trimAll } from "@/shared/helpers";
import { formatPhoneNumber } from "@/shared/helpers/commonHelpers";
import { useHasFormChanges } from "@/shared/hooks";
import { FileUpload } from "@ab.uitools/ui-library";
import { CollapseGroup } from "@ab.uitools/ui-library/components/Collapse";
import { FormField } from "@ab.uitools/ui-library/components/FormField";
import { Input } from "@ab.uitools/ui-library/components/Input";
import { Textarea } from "@ab.uitools/ui-library/components/Textarea";
import { useFormProps } from "@ab.uitools/ui-library/hooks/useFormProps";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import uuid4 from "uuid4";


export const UserOpeningApplicationForm = ({ isEdit, isCopy }: IOperationPageProps) => {
  const { t } = useTranslation();
  const { operationId = "" } = useParams();
  const idempotencyKey = useRef(uuid4()).current;
  const { errors = {}, setValue, watch, reset } = useFormProps();

  const [files, setFiles] = useState<File[]>();
  const [defaultData, setDefaultData] = useState<IExtendedObject>({});

  const {
    data: userOpeningApplication,
    isLoading: isGetApplicationLoading,
  } = useGetUserOpeningApplicationQuery({
    id: operationId
  }, {
    refetchOnMountOrArgChange: true,
    skip: (!isEdit || !isCopy) && !operationId
  });

  const [
    createUserOpeningApplicationApi,
    { isLoading: isCreateLoading }
  ] = useCreateUserOpeningApplicationMutation();

  const [
    updateUserOpeningApplicationApi,
    { isLoading: isUpdateLoading }
  ] = useUpdateUserOpeningApplicationMutation();

  const getFiles = (_files: File[]) => {
    if (_files && _files.length) {
      setFiles(_files);
      setValue(ApplicationKeys.DocumentFile, _files, {
        shouldValidate: true,
      });
    }
  };

  const removeSelectedFile = () => {
    setFiles([]);
    setValue(ApplicationKeys.DocumentFile, null, {
      shouldValidate: true,
    });
  };

  const userOpeningApplicationAction = useCallback((values: IUserOpeningApplicationForm) => {
    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (value === undefined || value === null) return;
      if (key === ApplicationKeys.Roles) {
        value.forEach((item: string) => {
          formData.append(ApplicationKeys.Roles, item);
        });
      } else if (key === ApplicationKeys.PhoneNumber) {
        formData.append(key, trimAll(value));
      } else if (key === ApplicationKeys.DocumentFile) {
        formData.append(key, value[0]);
      } else {
        formData.append(key, value);
      }
    });

    const requestData = {
      data: formData,
      headers: {
        "Content-Type": "multipart/form-data",
      }
    };

    return isEdit ? updateUserOpeningApplicationApi({
        ...requestData,
        id: `${userOpeningApplication?.id}`,
      }).unwrap() :
      createUserOpeningApplicationApi({
        ...requestData,
        idempotencyKey,
      }).unwrap();
  }, [userOpeningApplication]);

  const roleOptions = getRoleOptions({ t });
  const allowedTypes = SUPPORTED_DOCUMENT_FILE_TYPES.join(", ");

  const values = watch?.() || {};

  const setDefaultValues = () => {
    const data = {
      username: userOpeningApplication?.username,
      name: userOpeningApplication?.name,
      surname: userOpeningApplication?.surname,
      emailAddress: userOpeningApplication?.emailAddress,
      phoneNumber: userOpeningApplication?.phoneNumber,
      passportData: userOpeningApplication?.passportData,
      roles: userOpeningApplication?.roles,
      purpose: userOpeningApplication?.purpose,
      applicationNumber: isEdit ?
        userOpeningApplication?.applicationNumber : values.applicationNumber,
    };
    reset?.(data);
    setDefaultData(data);
  };

  const currentData = useMemo(() => ({
    username: values.username,
    name: values.name,
    surname: values.surname,
    emailAddress: values.emailAddress,
    phoneNumber: values.phoneNumber,
    passportData: values.passportData,
    roles: values.roles,
    purpose: values.purpose,
    applicationNumber: values.applicationNumber,
  }), [values]);

  const hasChanges = useHasFormChanges(defaultData, currentData);

  useEffect(() => {
    if ((isEdit || isCopy) && !!userOpeningApplication?.id) {
      setDefaultValues();
    }
  }, [userOpeningApplication]);

  return (
    <ApplicationLayout
      title={t("application.userOpening.title")}
      page="User opening application"
      isEdit={isEdit}
      hasChanges={hasChanges}
      isLoading={isCreateLoading || isUpdateLoading}
      isGetApplicationLoading={isGetApplicationLoading}
      getApplicationId={userOpeningApplicationAction}
    >
      <CollapseGroup
        className="collapse-group"
        items={[
          {
            content: <>
              <FormField
                name="username"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("general.username")}
                    placeholder={t("general.enterUsername")}
                  />
                )}
              />
              <FormField
                name="name"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("general.name")}
                    placeholder={t("general.enterName")}
                  />
                )}
              />
              <FormField
                name="surname"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("general.surname")}
                    placeholder={t("general.enterSurname")}
                  />
                )}
              />
              <FormField
                name="documentFile"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <FileUpload
                    {...props}
                    required
                    name="documentFile"
                    multiple={false}
                    getFiles={getFiles}
                    removeFiles={removeSelectedFile}
                    buttonText={t("general.attachFile")}
                    uploadedFiles={files}
                    helperText={t("application.userOpening.documentFile.helperText")}
                    hasError={!!errors.documentFile?.message}
                    allowedTypes={allowedTypes}
                  />
                )}
              />
              <FormField
                name="passportData"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.userOpening.passportData")}
                    placeholder={t("application.userOpening.enterPassportData")}
                  />
                )}
              />
              <FormField
                name="phoneNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => {
                  const { onChange, ...rest } = props;
                  return (
                    <Input
                      {...rest}
                      required
                      label={t("general.phoneNumber")}
                      placeholder={t("general.enterPhoneNumber")}
                      onChange={(e) => {
                        e.target.value = formatPhoneNumber(e.target.value);
                        onChange?.(e);
                      }}
                      isAllowed={({ formattedValue }) => isValidPhoneNumber(formattedValue)}
                    />
                  );
                }}
              />
              <FormField
                name="emailAddress"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("general.email")}
                    placeholder={t("general.enterEmail")}
                  />
                )}
              />
              <FormField
                name="purpose"
                className="mb-12"
                errorMessageIcon="infoFilled"
                hideErrorMessage
                As={(props) => (
                  <Textarea
                    {...props}
                    required
                    error={`${errors?.purpose?.message || ""}`}
                    label={t("general.purpose")}
                    placeHolder={t("general.enterYourPurpose")}
                    maxCount={INPUT_MAX_LIMIT}
                  />
                )}
              />
              <FormField
                name="roles"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Select
                    {...props}
                    isMulti
                    required
                    isCheckbox
                    isClearable
                    showCount={3}
                    options={roleOptions}
                    size="large"
                    label={t("application.userOpening.role")}
                    placeholder={t("application.userOpening.chooseRole")}
                  />
                )}
              />
            </>,
            title: t("application.applicationDetails"),
            value: "application_details",
            isOpen: true,
          },
          {
            content: <>
              <FormField
                name="applicationNumber"
                className="mb-32"
                errorMessageIcon="infoFilled"
                As={(props) => (
                  <Input
                    {...props}
                    required
                    label={t("application.applicationNumber")}
                    placeholder={t("application.enterApplicationNumber")}
                  />
                )}
              />
            </>,
            title: t("general.otherDetails"),
            value: "other_details",
            isOpen: true,
          },
        ]}
      />
    </ApplicationLayout>
  );
};
